import React from 'react'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarSize,
} from '@/components/ui/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { UserEntity } from '@/types/entities'

export type AvatarableUser = Pick<
  UserEntity,
  'fullName' | 'avatar' | 'initials'
>

interface Props {
  user: AvatarableUser
  size?: AvatarSize
  tooltip?: React.ReactElement | string
  /** @todo aim to remove this */
  style?: React.CSSProperties
  className?: string
}

const PASTEL_COLORS = [
  '#FFB5BA', // pastel pink
  '#B5DEFF', // pastel blue
  '#BAFFC9', // pastel green
  '#FFE4B5', // pastel orange
  '#E0B5FF', // pastel purple
  '#B5FFE9', // pastel mint
]

const UserAvatar: React.FC<Props> = ({
  user,
  tooltip,
  size = 'lg',
  style,
  className,
}) => {
  /**
   * Picks a colour, using the full name as a seed, ensuring the same user
   * always gets the same colour.
   */
  function getColour(): string {
    let seedNumber = 0

    user.fullName.split('').forEach((char) => {
      seedNumber += char.charCodeAt(0)
    })

    return PASTEL_COLORS[seedNumber % PASTEL_COLORS.length] as string
  }

  const avatar = (
    <Avatar size={size} style={style} className={className}>
      {user.avatar && <AvatarImage src={user.avatar} alt={user.fullName} />}

      <AvatarFallback
        className="font-bold"
        style={{
          backgroundColor: getColour(),
          // A darker version of the BG colour
          color: `lch(from ${getColour()} 40 c h)`,
        }}
      >
        {user.initials.toUpperCase()}
      </AvatarFallback>
    </Avatar>
  )

  if (!tooltip) {
    return avatar
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatar}</TooltipTrigger>
      <TooltipContent side="top">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

export default UserAvatar
